import { Flex, Typography } from "../../../../../shared/frontend/ui";
import { DocumentViewer } from "../../../../../shared/frontend/components/DocumentViewer";
import React from "react";
import { spacing } from "../app/design-system";
import { useI18n } from "../app/i18n/useI18n";
import { renderDocumentViewerLoading } from "./renderDocumentViewerLoading";
import { ThemedModal } from "./ThemedModal";
import { windowHeight } from "../../../../../shared/frontend/theme/displayZoom";

export type DocumentPdfPreviewModalProps = {
  open: boolean;
  title?: React.ReactNode;
  fileName?: string;
  fileData: Uint8Array | null;
  onCancel: () => void;
  width?: number | string;
  loading?: boolean;
};

/**
 * Zentrales PDF-Vorschau-Modal: ThemedModal um den gemeinsamen `DocumentViewer`, dessen Leiste
 * Zoom, Seitenwechsel, Drehen, Vollbild und Herunterladen bietet.
 */
export function DocumentPdfPreviewModal({
  open,
  title,
  fileName,
  fileData,
  onCancel,
  width = 960,
}: DocumentPdfPreviewModalProps) {
  const { text } = useI18n();
  const handleDownload = React.useCallback(() => {
    if (!fileData) return;
    const blob = new Blob([fileData.buffer as ArrayBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fallbackName = typeof title === "string" && title.trim().length > 0
      ? `${title.trim().toLowerCase().replace(/[^a-z0-9äöüß_-]+/gi, "-")}.pdf`
      : "document.pdf";
    const effectiveName = typeof fileName === "string" && fileName.trim().length > 0
      ? (fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`)
      : fallbackName;
    link.download = effectiveName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [fileData, fileName, title]);

  const modalTitle = (
    <Flex align="center" justify="space-between" style={{ width: "100%", paddingRight: spacing.xl }}>
      <Typography.Text strong ellipsis style={{ maxWidth: 360 }}>
        {title ?? text("Print preview")}
      </Typography.Text>
    </Flex>
  );

  return (
    <ThemedModal
      open={open && fileData != null}
      title={modalTitle}
      width={width}
      footer={null}
      onCancel={onCancel}
      autoFocusFirstField={false}
      destroyOnClose
      styles={{ body: { height: `min(${windowHeight("80vh")}, 850px)`, padding: 0 } }}
    >
      {fileData ? (
        <DocumentViewer
          kind="pdf"
          fileData={fileData}
          title={typeof title === "string" ? title : text("Print preview")}
          loadingText={text("Loading…")}
          errorText={text("PDF preview could not be loaded.")}
          renderLoading={renderDocumentViewerLoading}
          translate={text}
          zoomStorageKey="m1member.pdf-modal"
          onDownload={handleDownload}
        />
      ) : null}
    </ThemedModal>
  );
}
